"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import ImageGallerySectionLib from "@/component/ImageGallerySectionLib";
import type { ColourGalleryVariant, ModelColour } from "./ModelColourShowcaseLib";
import type { ColourGalleryImage } from "./ColourGalleryPopup";
import { getCarImageName, getCarImageViewLabel } from "@/component/pdp/image-page/galleryData";
import type { GallerySubcategory } from "@/component/pdp/image-page/types";

const ColourGalleryPopup = dynamic(() => import("./ColourGalleryPopup"));

export type ModelImageGalleryLibProps = {
    brandName: string;
    modelName: string;
    images: string[];
    galleryImages?: ColourGalleryImage[];
    gallerySubcategories?: GallerySubcategory[];
    colours: ModelColour[];
    variants?: ColourGalleryVariant[];
    modelId?: string;
    variantId?: string | null;
    isUpcoming?: boolean;
};

export default function ModelImageGalleryLib({
    brandName,
    modelName,
    images,
    galleryImages: categorisedImages,
    gallerySubcategories,
    colours,
    variants = [],
    modelId,
    variantId,
    isUpcoming = false,
}: ModelImageGalleryLibProps) {
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [startIndex, setStartIndex] = useState(0);

    const carName = `${brandName} ${modelName}`.trim();
    const sourceImages: Array<{ src: string; label?: string; alt?: string; title?: string }> =
        categorisedImages?.length
            ? categorisedImages.filter((image) => Boolean(image?.src))
            : images.filter(Boolean).map((src) => ({ src }));

    // Same naming as the colours gallery modal: "{car} {view} Image", no "image 1".
    const galleryImages = sourceImages.map((image, index) => {
        const name = getCarImageName(carName, image);
        return {
            id: index,
            src: image.src,
            alt: name,
            title: name,
            label: getCarImageViewLabel(carName, image) || undefined,
        };
    });

    if (!galleryImages.length) return null;

    const openPopup = (index: number) => {
        setStartIndex(index);
        setIsGalleryOpen(true);
    };

    return (
        <>
            <ImageGallerySectionLib
                headingLevel={2}
                className="mb-4 md:mb-6"
                data={{
                    title: `${brandName} ${modelName} Interior & Exterior Images`,
                    description: `Explore all available images for ${brandName} ${modelName}, including exterior, interior and colours.`,
                    viewAllLabel: "View All Images",
                    images: galleryImages,
                }}
                imageCount={galleryImages.length}
                onImageClick={(index) => openPopup(index)}
                onViewAllClick={() => openPopup(0)}
            />

            <ColourGalleryPopup
                open={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                brandName={brandName}
                modelName={modelName}
                colours={colours}
                images={images}
                galleryImages={categorisedImages}
                gallerySubcategories={gallerySubcategories}
                variants={variants}
                startIndex={startIndex}
                initialTab="all"
                modelId={modelId}
                variantId={variantId}
                isUpcoming={isUpcoming}
            />
        </>
    );
}
